import type { EntryValueViewT, EntryViewT } from "@/types/database/entries";
import { UUIDs } from "@/types/uuids";
import type { ChartConfig } from "../types";

// FAAM specific input IDs that need preprocessing
const FAAM_INPUT_IDS = [
	UUIDs.faamInputs.adlSubscale,
	UUIDs.faamInputs.sportsSubscale,
	UUIDs.faamInputs.totalScore,
];

/**
 * Preprocesses FAAM entries to convert text values like '80/84' into numeric values
 * while preserving the original text for tooltip display
 */
export function preprocessFaamEntries(entries: EntryViewT[]): EntryViewT[] {
	return entries.map((entry) => {
		const processedEntryValues = entry.entry_values?.map((entryValue) => {
			// Only process FAAM input IDs
			if (
				!FAAM_INPUT_IDS.includes(
					entryValue.input_id as (typeof FAAM_INPUT_IDS)[number],
				)
			) {
				return entryValue;
			}

			// Parse the text_value (e.g., '80/84' -> 80)
			const textValue = entryValue.text_value;
			if (typeof textValue === "string" && textValue.includes("/")) {
				const [numerator, denominator] = textValue.split("/");
				const numericNumerator = Number(numerator);
				const numericDenominator = Number(denominator);

				if (
					!Number.isNaN(numericNumerator) &&
					!Number.isNaN(numericDenominator) &&
					numericDenominator > 0
				) {
					const percentage = (numericNumerator / numericDenominator) * 100;
					return {
						...entryValue,
						number_value: percentage,
						input_key: "number", // Change to number so existing functions work
						// Keep original text_value for tooltip
					};
				}
			}

			return entryValue;
		});

		return {
			...entry,
			entry_values: processedEntryValues,
		} as EntryViewT;
	});
}

export function isEntryValueViewT(value: unknown): value is EntryValueViewT {
	if (!value || typeof value !== "object") return false;
	const obj = value as Record<string, unknown>;
	return "input_key" in obj && "input_label" in obj && "number_value" in obj;
}

interface ChartEntry {
	date: string;
	timing?: string;
	timing_label?: string;
	[key: string]: string | number | undefined;
}

const chartableInputKeys = ["number", "pain-slider", "single-choice"];

function filterEntryByFormId(entries: EntryViewT[], formId: string) {
	return entries.filter((e) =>
		e.entry_values?.some((ev: EntryValueViewT) => ev?.form_id === formId),
	);
}

export function convertEntriesToChartConfig(
	entries: EntryViewT[],
	formId: string,
): ChartConfig {
	const chartConfig: ChartConfig = {};
	const colors = [
		"hsl(var(--chart-1))",
		"hsl(var(--chart-2))",
		"hsl(var(--chart-3))",
		"hsl(var(--chart-4))",
		"hsl(var(--chart-5))",
	];

	// Get all unique input labels from the entries
	const inputLabels = new Set<string>();
	const formEntries = filterEntryByFormId(entries, formId);

	for (const formEntry of formEntries) {
		for (const value of formEntry.entry_values ?? []) {
			if (chartableInputKeys.includes(value.input_key ?? "")) {
				const label = value.input_label || "value";
				inputLabels.add(label);
			}
		}
	}

	// Create config for each input label
	let colorIndex = 0;
	for (const label of inputLabels) {
		const labelString: string = label; // Ensure it's a string
		chartConfig[labelString] = {
			label: labelString,
			color: colors[colorIndex % colors.length],
		};
		colorIndex++;
	}

	return chartConfig;
}

export function convertEntriesToChartData(
	entries: EntryViewT[],
	formId: string,
): ChartEntry[] {
	const entriesFromForm = entries.filter((e) =>
		e.entry_values?.some((ev: EntryValueViewT) => ev?.form_id === formId),
	);

	return entriesFromForm.map((entry) => {
		const formattedEntry: ChartEntry = {
			date: entry.calculated_entry_time ?? new Date().toISOString(),
			timing: entry.timing_label ?? undefined,
			timing_label: entry.timing_label ?? "No timing",
		};

		const formEntryValues = entry.entry_values?.filter((ev: EntryValueViewT) =>
			chartableInputKeys.includes(ev.input_key ?? ""),
		);

		// Add each entry value as a property using its input_label
		for (const ev of formEntryValues ?? []) {
			const label = ev.input_label ?? "value";
			if (ev.input_key === "single-choice") {
				// For single-choice inputs, use the text_value
				const textValue = ev.text_value;
				formattedEntry[label] =
					typeof textValue === "string" ? textValue : String(textValue ?? "");
			} else {
				// For numeric inputs, use the number_value
				formattedEntry[label] = Number(ev.number_value);
			}
		}

		return formattedEntry;
	});
}
